import {
  Box,
  Image,
  Stack,
  AspectRatio,
  Group,
  Text,
  Anchor,
} from "@mantine/core"
import type { BoxProps } from "@mantine/core"
import { DefaultModal } from "./default-modal"
import { DownloadFileButton } from "./DownloadFileButton"
import { getFileType } from "@/utils/getFileType"
import { ToggleMaximizeActionIcon } from "./ToggleMaximizeActionIcon"
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch"

export interface FilePreviewProps extends BoxProps {
  src: string
  fileName: string
  withDownload?: boolean
  onLoad?: React.ReactEventHandler<
    HTMLImageElement | HTMLVideoElement | HTMLIFrameElement | HTMLAudioElement
  >
}

const previewTypes = ["image", "video", "audio"]

export function FilePreview({
  src,
  fileName,
  withDownload = true,
  onLoad,
  ...boxProps
}: FilePreviewProps): React.ReactNode {
  const [opened, setOpened] = React.useState(false)
  const fileType = getFileType(fileName)
  const isPreviewType = previewTypes.includes(fileType)
  const DownloadIcon = React.memo(
    () =>
      withDownload && (
        <DownloadFileButton variant="icon" src={src} fileName={fileName} />
      )
  )

  function renderPreview(inModal = false) {
    const commonStyle = inModal
      ? { width: "100%", maxHeight: "90vh" as const }
      : { width: "100%", height: "100%" as const }

    switch (fileType) {
      case "image":
        if (inModal) {
          return (
            <div
              style={{
                width: "100%",
                height: maximized ? "calc(100vh - 80px)" : "80vh",
                overflow: "hidden",
              }}
            >
              <TransformWrapper
                wheel={{
                  step: 0.01,
                }}
                doubleClick={{ mode: "reset" }}
                pinch={{
                  step: 2,
                }}
                panning={{
                  velocityDisabled: true,
                }}
                limitToBounds={true}
              >
                <TransformComponent
                  wrapperStyle={{
                    width: "100%",
                    height: "100%",
                  }}
                  contentStyle={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    willChange: "transform",
                  }}
                >
                  <img
                    src={src}
                    onLoad={onLoad as any}
                    alt={fileName}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                      margin: "0 auto",
                      transition: "transform 0.15s ease-out",
                      willChange: "transform",
                    }}
                    draggable={false}
                  />
                </TransformComponent>
              </TransformWrapper>
            </div>
          )
        }

        return (
          <AspectRatio ratio={4 / 3}>
            <Image
              src={src}
              onLoad={onLoad}
              alt={fileName}
              fit="contain"
              style={commonStyle}
            />
          </AspectRatio>
        )
      case "video":
        return <video src={src} controls onLoad={onLoad} style={commonStyle} />
      case "audio":
        return (
          <audio src={src} controls onLoad={onLoad} style={{ width: "100%" }} />
        )
      case "pdf":
        return (
          <Stack h={"100%"}>
            <iframe
              src={src}
              datatype="application/pdf"
              width={"100%"}
              height={"100%"}
            ></iframe>
            <Group px={5} pb={25} justify="space-between" wrap="nowrap">
              <Anchor href={src} target="_blank">
                {fileName}
              </Anchor>
              <DownloadIcon />
            </Group>
          </Stack>
        )
      default:
        return (
          <Group justify="space-between" wrap="nowrap">
            <Text>{fileName}</Text>
            <DownloadIcon />
          </Group>
        )
    }
  }

  const [maximized, setMaximized] = React.useState(false)

  function toggleMaximize(newMaximized: boolean) {
    setMaximized(newMaximized)
  }

  return (
    <>
      <Box
        {...boxProps}
        onClick={() => isPreviewType && setOpened(true)}
        style={{
          cursor: isPreviewType ? "pointer" : undefined,
          overflow: "hidden",
          borderRadius: 8,
          ...boxProps.style,
        }}
      >
        {renderPreview()}
      </Box>

      <DefaultModal
        opened={opened}
        onClose={() => {
          setOpened(false)
          setMaximized(false)
        }}
        // size="auto"
        size={"xl"}
        fullScreen={maximized}
        centered
        title={
          <Group>
            <ToggleMaximizeActionIcon onClick={toggleMaximize} />
            <DownloadIcon />
          </Group>
        }
      >
        <Stack>{renderPreview(true)}</Stack>
      </DefaultModal>
    </>
  )
}
